import { Autocomplete, Box, Button, ConfirmDialog } from '@planitgeo/treeplotter-ui-library';
import { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { sharedStyles } from 'styles/sharedStyles';
import { useLocales } from 'locales';
import TenantRepository from 'common/infrastructure/tenant/repository';
import { clearTenantSelection } from 'identityAndAccess/infrastructure/auth/redux';
import { RootStoreType } from 'redux/rootReducer';
import { Tenant } from 'api-client/model/tenant';

export default function TenantSwitchDialog() {
  const { tenantEntities: tenants } = TenantRepository.useTenants();
  const authState = useSelector((state: RootStoreType) => state.auth);
  const { translate } = useLocales();
  const dispatch = useDispatch();
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);

  const handleConfirm = useCallback(async () => {
    if (!selectedTenantId || isSwitching) return;
    try {
      setIsSwitching(true);
      await Promise.resolve(TenantRepository.saveCurrent(selectedTenantId));
      setDialogOpen(false);
      dispatch(clearTenantSelection());
    } finally {
      setIsSwitching(false);
    }
  }, [selectedTenantId, dispatch, isSwitching]);

  useEffect(() => {
    // Show dialog if user just logged in and has multiple tenants
    if (authState.showTenantSelection && tenants && tenants.length > 1) {
      setDialogOpen(true);
    }
    // If user has only one tenant or no showTenantSelection flag, clear the flag
    else if (authState.showTenantSelection && tenants && tenants.length <= 1) {
      dispatch(clearTenantSelection());
    }
  }, [authState.showTenantSelection, tenants, dispatch]);

  return (
    <ConfirmDialog
      open={dialogOpen}
      title={translate('tenant_switch_dialog_title')}
      message={translate('tenant_switch_dialog_message')}
      content={
        <TenantSwitchContent
          tenants={tenants || []}
          selectedTenantId={selectedTenantId}
          onTenantChange={setSelectedTenantId}
        />
      }
      action={
        <Button
          color="primary"
          shape="pill"
          disabled={!selectedTenantId}
          onClick={handleConfirm}
          data-testid="tenant-switch-confirm-button"
          sx={{ paddingX: 4 }}
        >
          {translate('confirm')}
        </Button>
      }
    />
  );
}

const TenantSwitchContent = ({
  tenants,
  selectedTenantId,
  onTenantChange,
}: {
  tenants: Tenant[];
  selectedTenantId: string;
  onTenantChange: (tenantId: string) => void;
}) => {
  const { translate } = useLocales();

  return (
    <Box sx={{ pt: 2, pb: 2 }}>
      <Autocomplete
        options={tenants}
        value={tenants.find((tenant) => tenant.id === selectedTenantId) ?? null}
        onChange={(_, value) => onTenantChange(value?.id ?? '')}
        getOptionLabel={(tenant) => tenant.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        label={translate('tenant_list_ui')}
        fullWidth
        sx={{ ...sharedStyles['input-label'] }}
        data-testid="tenant-switch-select"
        renderOption={(props, option) => (
          <li {...props} key={option.id} data-testid={`tenant-switch-select-item-${option.id}`}>
            {option.name}
          </li>
        )}
      />
    </Box>
  );
};
